/**
 * Monitoramento do tempo-real: o batimento que o servidor manda e o ping que
 * o cliente usa para medir latência (indicador de WebSocket na navbar, #4147).
 *
 * Sem Contract aqui — nenhum dos dois tem rota REST associada; é tráfego de
 * socket puro, nos dois sentidos.
 *
 * ── Por que os INTERVALOS moram aqui ─────────────────────────────────────
 *
 * Este é o caso em que a duplicação já estava anotada no código: o backend
 * declarava `SERVER_HEARTBEAT_MS = 5000` e o frontend declarava
 * `HEARTBEAT_MS = 5000` com o comentário "Mantê-los alinhados". Um número
 * repetido em dois arquivos, com um pedido escrito para não divergirem, é um
 * bug esperando acontecer: quem aumentar o intervalo no servidor e esquecer o
 * front faz o front concluir que o tempo-real travou (ele espera batimento a
 * cada 2,5 intervalos) e acender alarme falso na navbar de todo mundo.
 *
 * Nome de evento não é o único tipo de contrato entre as pontas — combinação
 * de tempo também é.
 */

export const MONITORING = {
  /** Servidor → cliente, em todos os namespaces de empresa. */
  HEARTBEAT: "server:heartbeat",
  /** Cliente → servidor, com ack. O servidor responde com IPongPayload. */
  PING: "client:ping"
} as const;

export const MONITORING_TIMING = {
  /** De quanto em quanto o servidor emite o batimento. */
  heartbeatMs: 5000,
  /**
   * Sem batimento por mais que isto, o cliente considera o tempo-real parado.
   * 2,5 intervalos: tolera um batimento perdido sem acusar, mas não dois.
   */
  staleAfterMs: 5000 * 2.5,
  /** De quanto em quanto o cliente mede latência. O limitador de taxa do servidor é dimensionado a partir daqui. */
  pingEveryMs: 10000,
  /** Prazo do pong. Acima disso a resposta não é mais informação de latência — é perda. */
  pingTimeoutMs: 5000
} as const;

/** Payload de MONITORING.HEARTBEAT. */
export interface HeartbeatPayload {
  serverTime: number;
}

/** O que o cliente manda no MONITORING.PING. */
export interface PingPayload {
  /** Carimbo do relógio do cliente; volta no pong para ele fechar a conta do RTT. */
  t0: number;
}

/** Atraso do event loop do processo do servidor, em ms. */
export interface EventLoopLag {
  mean: number;
  p99: number;
}

/**
 * Resposta normal do ping. O `clientTime` volta como veio (ou null): quem
 * calcula o RTT é o cliente, com o próprio relógio dos dois lados da conta —
 * comparar relógio de servidor com o de navegador mediria clock skew, não
 * latência.
 */
export interface PongPayload {
  clientTime: number | null;
  serverRecv: number;
  serverSend: number;
  serverProcessMs: number;
  loopLag: EventLoopLag;
}

/**
 * Resposta quando o cliente passou do limite de taxa. Barata de propósito
 * (não lê o histograma): o cliente honesto precisa distinguir "fui limitado"
 * de "meu pacote se perdeu".
 */
export interface PongThrottled {
  throttled: true;
  retryAfterMs: number;
}

export type PongResposta = PongPayload | PongThrottled;
